import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { TypedMemo } from '../../../common/types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { IntegrationTypeIcon } from '../../../music24/components/IntegrationTypeIcon/IntegrationTypeIcon';
import { Music24Logo } from '../../../music24/components/Music24Logo';
import { Panel } from '../../../music24/components/primitives/Panel';
import { useSoundCloudReturn } from './useSoundCloudReturn';
import { SoundCloudReturnSteps } from './SoundCloudReturnSteps';
import { soundCloudReturnMessages as m } from './soundCloudReturnMessages';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for SoundCloudReturn; each part uses its own slice of it */
import styles from './SoundCloudReturn.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * `/app/catalog/connect/soundCloud?state=…` (design b): MusicAPI sends the browser back here after
 * the SoundCloud consent. The page finishes the connection, then goes on to Channels with the result
 * (`?soundCloud=`), as every social connection does (owner, 2026-10-16). A sign-in from the "Where
 * it plays" panel goes back to that page (`returnTo`) after a short "connected" step.
 */
const SoundCloudReturnPageComponent: React.FC<{ state: string }> = ({ state }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const result = useSoundCloudReturn(state);
  const goesBack = result.kind === 'connected' && result.returnTo !== null;

  useEffect(() => {
    if (result.kind === 'working' || goesBack) {
      return;
    }
    router.replace(SMLRoutes.promoteChannels, { soundCloud: result.kind === 'connected' ? 'connected' : result.error });
  }, [goesBack, result, router]);

  const content =
    result.kind === 'connected' ? (
      <>
        <div>
          <h2>{intl.formatMessage(m.connectedTitle)}</h2>
          <p>{withBrandWord(intl.formatMessage(m.connectedBody))}</p>
        </div>
        {result.artist !== null && (
          <div className={styles.who}>
            {result.artist.imageUrl !== null && <img src={result.artist.imageUrl} alt="" />}
            <b className={styles.grow}>{result.artist.name}</b>
            <span className={styles.pill}>{intl.formatMessage(m.connectedPill)}</span>
          </div>
        )}
      </>
    ) : (
      <>
        <div>
          <h2>{intl.formatMessage(m.workingTitle)}</h2>
          <p>{withBrandWord(intl.formatMessage(m.workingBody))}</p>
        </div>
        <SoundCloudReturnSteps />
      </>
    );

  return (
    <div className={styles.page}>
      <Panel className={styles.card} data-testid={`soundcloud-return-${result.kind}`}>
        <div className={styles.marks} aria-hidden="true">
          <Music24Logo size={30} />
          <span>⟷</span>
          <IntegrationTypeIcon integrationType={MusicAPIIntegrationID.SoundCloud} size={36} />
        </div>
        {content}
      </Panel>
    </div>
  );
};

export const SoundCloudReturnPage = TypedMemo(SoundCloudReturnPageComponent);
